"use client";

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ZoomIn, ZoomOut, Download } from "lucide-react";

interface LightboxCtx {
  open: (url: string, alt?: string) => void;
  close: () => void;
}

const Ctx = createContext<LightboxCtx | null>(null);

export function useImageLightbox() {
  const ctx = useContext(Ctx);
  if (!ctx) {
    // Graceful no-op if provider is missing (e.g. SSR edge cases)
    return { open: () => {}, close: () => {} } satisfies LightboxCtx;
  }
  return ctx;
}

export default function ImageLightboxProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<{ url: string; alt: string } | null>(null);
  const [zoomed, setZoomed] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const open = useCallback((url: string, alt = "") => {
    if (!url) return;
    setState({ url, alt });
    setZoomed(false);
  }, []);

  const close = useCallback(() => {
    setState(null);
    setZoomed(false);
  }, []);

  // Close on Esc + lock body scroll
  useEffect(() => {
    if (!state) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [state, close]);

  return (
    <Ctx.Provider value={{ open, close }}>
      {children}
      {mounted && createPortal(
        <AnimatePresence>
          {state && (
            <motion.div
              key="lightbox"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              onClick={close}
              className="fixed inset-0 z-[1000] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
            >
              {/* Controls */}
              <div className="absolute top-3 right-3 sm:top-4 sm:right-4 flex items-center gap-2 z-10">
                <a
                  href={state.url}
                  download
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur transition-colors"
                  title="Otwórz w nowej karcie"
                >
                  <Download className="w-4 h-4" />
                </a>
                <button
                  onClick={(e) => { e.stopPropagation(); setZoomed((z) => !z); }}
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur transition-colors"
                  title={zoomed ? "Pomniejsz" : "Powiększ"}
                >
                  {zoomed ? <ZoomOut className="w-4 h-4" /> : <ZoomIn className="w-4 h-4" />}
                </button>
                <button
                  onClick={close}
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur transition-colors"
                  title="Zamknij (Esc)"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Image container */}
              <motion.div
                initial={{ scale: 0.92, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.92, opacity: 0 }}
                transition={{ type: "spring", damping: 24, stiffness: 280 }}
                className={`relative ${zoomed ? "overflow-auto w-full h-full" : "flex items-center justify-center"}`}
                onClick={(e) => e.stopPropagation()}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={state.url}
                  alt={state.alt}
                  onClick={() => setZoomed((z) => !z)}
                  className={`select-none ${zoomed
                    ? "max-w-none cursor-zoom-out"
                    : "max-w-[92vw] max-h-[88vh] cursor-zoom-in object-contain"
                  } rounded-lg shadow-2xl`}
                  draggable={false}
                />
              </motion.div>

              {state.alt && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs sm:text-sm text-white/80 bg-black/40 px-3 py-1.5 rounded-full backdrop-blur max-w-[90vw] truncate">
                  {state.alt}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </Ctx.Provider>
  );
}

/** Convenience wrapper — clickable <img> that opens the lightbox on click. */
export function PreviewableImage({
  src,
  alt = "",
  className = "",
  wrapperClassName = "",
}: {
  src: string;
  alt?: string;
  className?: string;
  wrapperClassName?: string;
}) {
  const { open } = useImageLightbox();
  if (!src) return null;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); open(src, alt); }}
      className={`group relative inline-block cursor-zoom-in ${wrapperClassName}`}
      title="Kliknij, aby powiększyć"
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={src} alt={alt} className={className} draggable={false} />
      <span className="pointer-events-none absolute inset-0 rounded-[inherit] ring-0 group-hover:ring-2 ring-blue-400/60 transition-all" />
    </button>
  );
}
